// src/pages/AdminCourseProgress.jsx
import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { getCourse, getLessons, getStudentEnrollments, getAllStudents, markLessonComplete, unmarkLessonComplete } from "../firebase/database";
import { useAuth } from "../context/AuthContext";

export default function AdminCourseProgress() {
  const { courseId } = useParams();
  const { user } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [marking, setMarking] = useState(null);
  const [dataReady, setDataReady] = useState(false);
  const [selectedLessons, setSelectedLessons] = useState([]);

  useEffect(() => {
    loadData();
  }, [courseId]);

  useEffect(() => {
    setSelectedLessons([]);
  }, [selectedStudentId, courseId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [courseData, lessonsData, allStudents] = await Promise.all([
        getCourse(courseId),
        getLessons(courseId),
        getAllStudents()
      ]);

      const enrolledStudents = allStudents.filter(s => s.role === "student" || !s.id.startsWith("STAFF-"));
      const enrollmentMap = {};
      
      for (const student of enrolledStudents) {
        const studentEnrollments = await getStudentEnrollments(student.id);
        const courseEnrollment = studentEnrollments.find(e => e.courseId === courseId);
        if (courseEnrollment) {
          enrollmentMap[student.id] = courseEnrollment;
        }
      }

      const actuallyEnrolled = enrolledStudents.filter(s => enrollmentMap[s.id]);

      setCourse(courseData);
      setLessons(lessonsData);
      setStudents(actuallyEnrolled);
      setEnrollments(enrollmentMap);
      
      if (actuallyEnrolled.length > 0) {
        setSelectedStudentId(actuallyEnrolled[0].id);
      }
      setDataReady(true);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const selectedStudent = useMemo(() => 
    students.find(s => s.id === selectedStudentId) || null
  , [students, selectedStudentId]);

  const formatDate = (iso) => {
    if (!iso) return "";
    const date = new Date(iso);
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const handleMarkComplete = async (lessonId) => {
    if (!selectedStudent) return;
    
    const enrollment = enrollments[selectedStudent.id];
    if (enrollment?.completedLessons?.some(l => l.lessonId === lessonId)) {
      return;
    }

    setMarking(lessonId);
    try {
      await markLessonComplete(selectedStudent.id, courseId, lessonId, user.uid, user.displayName || user.email || "Staff");
      setSelectedLessons(prev => prev.filter(id => id !== lessonId));
      await loadData();
    } catch (err) {
      console.error("Error marking complete:", err);
      alert("Failed to mark lesson complete");
    } finally {
      setMarking(null);
    }
  };

  const handleMarkSelectedComplete = async () => {
    if (!selectedStudent || selectedLessons.length === 0) return;
    
    setMarking("bulk");
    try {
      for (const lessonId of selectedLessons) {
        const enrollment = enrollments[selectedStudent.id];
        if (enrollment?.completedLessons?.some(l => l.lessonId === lessonId)) {
          continue;
        }
        await markLessonComplete(selectedStudent.id, courseId, lessonId, user.uid, user.displayName || user.email || "Staff");
      }
      setSelectedLessons([]);
      await loadData();
    } catch (err) {
      console.error("Error marking selected complete:", err);
      alert("Failed to mark selected lessons complete");
    } finally {
      setMarking(null);
    }
  };

  const handleLessonSelectToggle = (lessonId) => {
    setSelectedLessons(prev => {
      if (prev.includes(lessonId)) {
        return prev.filter(id => id !== lessonId);
      } else {
        return [...prev, lessonId];
      }
    });
  };

  const handleSelectAllToggle = () => {
    if (selectedLessons.length === incompleteLessons.length) {
      setSelectedLessons([]);
    } else {
      setSelectedLessons(incompleteLessons.map(l => l.id));
    }
  };

  const handleUnmarkComplete = async (lessonId) => {
    if (!selectedStudent) return;
    if (!window.confirm("Are you sure you want to unmark this lesson? The student will need to complete it again.")) {
      return;
    }

    setMarking(lessonId);
    try {
      await unmarkLessonComplete(selectedStudent.id, courseId, lessonId);
      await loadData();
    } catch (err) {
      console.error("Error unmarking complete:", err);
      alert("Failed to unmark lesson");
    } finally {
      setMarking(null);
    }
  };

  const enrollment = selectedStudent ? enrollments[selectedStudent.id] : null;
  const completedLessons = enrollment?.completedLessons || [];
  const completedIds = completedLessons.map(l => l.lessonId);
  const currentIndex = enrollment?.currentLessonIndex || 0;
  
  // HOOKS MOVED TO TOP LEVEL TO PREVENT CONDITIONAL HOOK CALLS
  const incompleteLessons = useMemo(() => {
    return lessons.filter(l => !completedIds.includes(l.id));
  }, [lessons, completedIds]);

  if (loading || !dataReady) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Loading...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>
        Course not found
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div style={{ maxWidth: 600, margin: "0 auto", padding: 24 }}>
        <div style={{ marginBottom: 24 }}>
          <Link 
            to="/admin/courses" 
            style={{ color: "#6B7280", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 16 }}
          >
            ← Back to Courses
          </Link>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
            Course Progress
          </h1>
          <p style={{ color: "#6B7280", margin: "4px 0 0 0", fontFamily: "DM Sans, sans-serif" }}>
            {course.name}
          </p>
        </div>

        <div style={{ 
          background: "white", 
          borderRadius: 12, 
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
          padding: 40, 
          textAlign: "center" 
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📚</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: "#1F2937", marginBottom: 8 }}>
            No Students Enrolled
          </div>
          <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 24 }}>
            Assign students to this course first to manage their progress.
          </div>
          <Link
            to={`/admin/courses/${courseId}/assign`}
            style={{
              padding: "12px 24px",
              background: "#3B82F6",
              color: "white",
              borderRadius: 8,
              textDecoration: "none",
              fontWeight: 600,
              fontFamily: "DM Sans, sans-serif",
              display: "inline-block",
            }}
          >
            Assign Students
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link 
          to="/admin/courses" 
          style={{ color: "#6B7280", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 16 }}
        >
          ← Back to Courses
        </Link>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
          Course Progress
        </h1>
        <p style={{ color: "#6B7280", margin: "4px 0 0 0", fontFamily: "DM Sans, sans-serif" }}>
          {course.name} — Mark lessons as complete for students
        </p>
      </div>

      {/* Student Selector */}
      <div style={{ 
        background: "white", 
        borderRadius: 12, 
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
        padding: 20, 
        marginBottom: 24 
      }}>
        <label style={{ 
          display: "block", 
          fontSize: 14, 
          fontWeight: 600, 
          color: "#374151", 
          marginBottom: 12,
          fontFamily: "DM Sans, sans-serif"
        }}>
          Select Student
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {students.map(student => (
            <button
              key={student.id}
              onClick={() => setSelectedStudentId(student.id)}
              style={{
                padding: "10px 16px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                fontFamily: "DM Sans, sans-serif",
                cursor: "pointer",
                border: "none",
                background: selectedStudentId === student.id ? "#3B82F6" : "#F3F4F6",
                color: selectedStudentId === student.id ? "white" : "#374151",
              }}
            >
              {student.name} ({student.roll || student.id})
            </button>
          ))}
        </div>
      </div>

      {selectedStudent && enrollment && (
        <>
          {/* Student Info */}
          <div style={{ 
            background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)", 
            borderRadius: 12, 
            padding: 20, 
            marginBottom: 24,
            color: "white"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px 0", fontFamily: "DM Sans, sans-serif" }}>
                  {selectedStudent.name}
                </h2>
                <div style={{ fontSize: 14, opacity: 0.9, fontFamily: "DM Sans, sans-serif" }}>
                  Roll: {selectedStudent.roll || selectedStudent.id}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}>
                  {completedLessons.length} / {lessons.length}
                </div>
                <div style={{ fontSize: 14, opacity: 0.8, fontFamily: "DM Sans, sans-serif" }}>Completed</div>
              </div>
            </div>
          </div>

          {/* Lessons List */}
          <div style={{ 
            background: "white", 
            borderRadius: 12, 
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
            overflow: "hidden" 
          }}>
            <div style={{ 
              padding: "16px 24px", 
              borderBottom: "1px solid #E5E7EB",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {incompleteLessons.length > 0 && (
                  <input
                    type="checkbox"
                    checked={selectedLessons.length === incompleteLessons.length && incompleteLessons.length > 0}
                    onChange={handleSelectAllToggle}
                    disabled={marking !== null}
                    style={{
                      width: 18,
                      height: 18,
                      cursor: "pointer"
                    }}
                  />
                )}
                <h3 style={{ fontSize: 16, fontWeight: 600, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
                  Lessons {selectedLessons.length > 0 ? `(${selectedLessons.length} selected)` : ""}
                </h3>
              </div>
              {selectedLessons.length > 0 && (
                <button
                  onClick={handleMarkSelectedComplete}
                  disabled={marking !== null}
                  style={{
                    padding: "8px 16px",
                    background: "#16A34A",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "DM Sans, sans-serif",
                    cursor: marking !== null ? "not-allowed" : "pointer",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                  }}
                >
                  {marking === "bulk" ? "Marking..." : "Mark Selected as Complete"}
                </button>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              {lessons.map((lesson, index) => {
                const isCompleted = completedIds.includes(lesson.id);
                const isCurrent = index === currentIndex;
                const completionInfo = completedLessons.find(l => l.lessonId === lesson.id);

                return (
                  <div 
                    key={lesson.id}
                    style={{ 
                      padding: "16px 24px", 
                      borderBottom: index < lessons.length - 1 ? "1px solid #E5E7EB" : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: isCompleted ? "#F0FDF4" : "transparent",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1 }}>
                      {!isCompleted ? (
                        <input
                          type="checkbox"
                          checked={selectedLessons.includes(lesson.id)}
                          onChange={() => handleLessonSelectToggle(lesson.id)}
                          disabled={marking !== null}
                          style={{
                            width: 18,
                            height: 18,
                            cursor: "pointer"
                          }}
                        />
                      ) : (
                        <div style={{ width: 18 }} />
                      )}
                      <div style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: isCompleted ? "#16A34A" : "#E5E7EB",
                        color: isCompleted ? "white" : "#9CA3AF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 16,
                        fontWeight: 600,
                      }}>
                        {isCompleted ? "✓" : index + 1}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: "#1F2937", fontFamily: "DM Sans, sans-serif" }}>
                          {lesson.title}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                          <span style={{
                            padding: "2px 10px",
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 500,
                            background: lesson.category === "class" ? "#DBEAFE" : "#D1FAE5",
                            color: lesson.category === "class" ? "#1D4ED8" : "#047857",
                          }}>
                            {lesson.category === "class" ? "Class" : "Practice"}
                          </span>
                          {isCompleted && completionInfo && (
                            <span style={{ fontSize: 12, color: "#16A34A" }}>
                              ✓ Completed by {completionInfo.completedByStaffName} on {formatDate(completionInfo.completedAt)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {isCompleted ? (
                      <button
                        onClick={() => handleUnmarkComplete(lesson.id)}
                        disabled={marking === lesson.id}
                        style={{
                          padding: "10px 20px",
                          background: marking === lesson.id ? "#9CA3AF" : "#FEF3C7",
                          color: marking === lesson.id ? "#9CA3AF" : "#D97706",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 600,
                          fontFamily: "DM Sans, sans-serif",
                          cursor: marking === lesson.id ? "not-allowed" : "pointer",
                        }}
                      >
                        {marking === lesson.id ? "..." : "Unmark"}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleMarkComplete(lesson.id)}
                        disabled={marking === lesson.id}
                        style={{
                          padding: "10px 20px",
                          background: marking === lesson.id ? "#9CA3AF" : "#16A34A",
                          color: "white",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 600,
                          fontFamily: "DM Sans, sans-serif",
                          cursor: marking === lesson.id ? "not-allowed" : "pointer",
                        }}
                      >
                        {marking === lesson.id ? "Marking..." : "Mark as Complete"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}